import { ImResponse, omsV2Client, resolve } from 'services/api';
import { TaskLog } from 'services/api/log';
import { transferPolicyContent } from 'services/api/oms';

export interface Page {
  pageNum: number;
  pageSize: number;
}

interface PlatformRegionType {
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion | 'ALL';
  scenarioType: Marketing.AllScenarioType;
}

export interface PolicyListParams extends PlatformRegionType, Page {
  round: number;
  policyValidType: 'ALL' | 'VALID' | 'INVALID';
  storeName?: string;
}

export interface PolicyListResponse {
  pages: Page & {
    totalRows: number;
  };
  policy: Aide.Policy[];
}

export function policyList(params: PolicyListParams) {
  return resolve(
    omsV2Client.get<ImResponse<PolicyListResponse>>('/policy', {
      params: {
        ...params,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    }),
  ).then((res) => {
    return {
      ...res,
      policy: res.policy.map(transferPolicyContent),
    };
  });
}

export function createPolicy(body: Omit<Aide.Policy, 'id' | 'priority'>) {
  return resolve(omsV2Client.post<ImResponse<{ policyId: number }>>('/policy', body));
}

export function updatePolicy(body: Omit<Aide.Policy, 'priority'>) {
  return omsV2Client.post('/policy', body);
}

export function policyDetail(id: string | number) {
  return resolve(omsV2Client.get<ImResponse<{ policy: Aide.Policy }>>(`/policy/${id}`)).then(
    (res) => {
      return {
        ...res,
        policy: transferPolicyContent(res.policy),
      };
    },
  );
}

export type UpdatePriorityAction = 'UP' | 'DOWN' | 'TOP' | 'BOTTOM';

export interface UpdatePolicyPriorityBody extends Omit<PolicyListParams, keyof Page> {
  policyId: number;
  action: UpdatePriorityAction;
}

export function updatePolicyPriority(body: UpdatePolicyPriorityBody) {
  return omsV2Client.post('/policy/priority', body);
}

export function deletePolicy(id: string | number) {
  return omsV2Client.delete(`/policy/${id}`);
}

export interface UnsetPolicyStore {
  storeId: string;
  storeName: string;
  alias: string;
  region: General.UpperCaseRegion;
  internalStoreId: number;
}

export function unsetPolicyStoreList({
  scenarioType,
  ...params
}: PlatformRegionType & { round: number }) {
  return resolve(
    omsV2Client.get<ImResponse<UnsetPolicyStore[]>>(`/reminder/store/unset/${scenarioType}`, {
      params,
    }),
  );
}

export interface StarConflictBody {
  scenarioType: Marketing.AllScenarioType;
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  stars: number[];
}

export interface StarConflict {
  star: number;
  conflictScenarios: Marketing.ReviewManagementScenarioType[];
}

export function starConflict(body: StarConflictBody) {
  return resolve(omsV2Client.post<ImResponse<StarConflict[]>>('review/star/conflict', body));
}

export interface ScenarioStore {
  internalId: number;
  externalId: string;
  name: string;
  alias: string;
  externalType: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
}

export function scenarioStoreList({ scenarioType, ...params }: PlatformRegionType) {
  return resolve(
    omsV2Client.get<ImResponse<{ stores: ScenarioStore[] }>>(`/store/${scenarioType}`, { params }),
  );
}

export function scenarioStatus({ scenarioType, ...params }: Omit<PlatformRegionType, 'region'>) {
  return resolve(
    omsV2Client.get<ImResponse<{ region: General.UpperCaseRegion; taskAllInvalid: boolean }[]>>(
      `/scenarios/status/${scenarioType}`,
      { params },
    ),
  );
}

export interface DetectConflictBody {
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  scenarioType: Marketing.AllScenarioType;
  stores: Marketing.TaskStore[];
}

export interface DetectConflictResponse {
  hasConflict: boolean;
  conflictPolicy: {
    policyId: number;
    policyName: string;
  }[];
}

export function detectConflict(body: DetectConflictBody) {
  return resolve(
    omsV2Client.post<ImResponse<DetectConflictResponse>>('/reminder/signPraiseConflict', body),
  );
}

export interface BindStoreBody {
  policyIds: number[];
  stores: UnsetPolicyStore[];
}

export function bindStore(body: BindStoreBody) {
  return omsV2Client.post('/policy/bindStore', body);
}

export interface LogListParams extends PlatformRegionType {
  round: number;
  pageSize: number;
  offset: number;
}

export function logList(params: LogListParams) {
  return resolve(
    omsV2Client.get<
      ImResponse<{
        hasNext: boolean;
        nextOffset: number;
        logs?: TaskLog[];
      }>
    >('/log/list', { params }),
  );
}

export function updatePolicyEnable(body: { policyId: number; enable: boolean }) {
  return omsV2Client.post('/policy/enable', body);
}

export interface RecommendTemplatesParams {
  scenarioType: Marketing.AllScenarioType;
  round: number;
  platform: General.PlatformUpperCase;
}

export function recommendTemplates(params: RecommendTemplatesParams) {
  return resolve(
    omsV2Client.get<
      ImResponse<{
        scenarioType: Marketing.AllScenarioType;
        langTemplateMap: Aide.LangTemplatesMap;
      }>
    >('/template', { params }),
  );
}

export function getTiktokEndSession(params?: { region: 'US' }) {
  return resolve(
    omsV2Client.get<ImResponse<{ policy: Aide.TiktokEndSession }>>('/policy/tiktokEndSession', {
      params,
    }),
  );
}
